import { DebouncedInput, DebouncedTextarea } from "@shared/components/inputs";
import theme from "@shared/utils/themeColors";
import React from "react";

interface SectionProps {
  title: string;
  children: React.ReactNode;
  className?: string;
}

export const EditorSection: React.FC<SectionProps> = ({
  title,
  children,
  className = "mb-6",
}) => {
  return (
    <div className={className}>
      <h3 className="text-lg font-medium mb-3 text-gray-800 dark:text-gray-200">
        {title}
      </h3>
      <div className="grid grid-cols-1 gap-4">{children}</div>
    </div>
  );
};

interface FieldProps {
  label?: string;
  children: React.ReactNode;
}

export const EditorField: React.FC<FieldProps> = ({ label, children }) => {
  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
        </label>
      )}
      {children}
    </div>
  );
};

interface TextFieldProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
}

export const EditorTextField: React.FC<TextFieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  rows,
}) => {
  return (
    <EditorField label={label}>
      <DebouncedTextarea
        value={value || ""}
        onChange={onChange}
        className={`w-full px-3 py-2 border ${theme.border.secondary} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white`}
        placeholder={placeholder}
        rows={rows}
      />
    </EditorField>
  );
};

interface InputFieldProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: "text" | "number";
}

export const EditorInputField: React.FC<InputFieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
}) => {
  return (
    <EditorField label={label}>
      <DebouncedInput
        type={type}
        value={value || ""}
        onChange={(value) => onChange(value as string)}
        className={`w-full px-3 py-2 border ${theme.border.secondary} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white`}
        placeholder={placeholder}
      />
    </EditorField>
  );
};
